<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <v-icon left>mdi-file-document</v-icon>
        Gestión de Facturas
      </v-card-title>
      
      <v-tabs v-model="activeTab" background-color="transparent" color="primary">
        <v-tab>
          <v-icon left>mdi-history</v-icon>
          Historial
        </v-tab>
        <v-tab>
          <v-icon left>mdi-receipt-text-plus</v-icon>
          Facturar
          <v-badge
            v-if="totalFacturables > 0"
            :content="totalFacturables"
            color="warning"
            inline
            class="ml-2"
          />
        </v-tab>
      </v-tabs>
      
      <v-card-text>
        <v-tabs-items v-model="activeTab">
          <!-- Tab: Historial -->
          <v-tab-item>
            <v-row class="mt-2">
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.estado"
                  :items="estados"
                  label="Estado"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.tipoComprobante"
                  :items="tiposComprobante"
                  label="Tipo"
                  outlined
                  dense
                  clearable
                />
              </v-col>
            </v-row>
            
            <div class="table-fixed-header-wrap">
            <v-data-table
              :headers="headers"
              :items="facturas"
              :loading="loading"
              :server-items-length="totalFacturas"
              :options.sync="options"
              :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
              fixed-header
              height="calc(100vh - 340px)"
              class="elevation-1 mt-2"
            >
              <template v-slot:item.fechaEmision="{ item }">
                {{ getDateTimeFormat(item.fechaEmision) }}
              </template>
              
              <template v-slot:item.tipoComprobante="{ item }">
                {{ getTipoComprobanteText(item.tipoComprobante) }}
              </template>
              
              <template v-slot:item.numero="{ item }">
                {{ String(item.puntoVenta).padStart(4, '0') }}-{{ String(item.numeroComprobante).padStart(8, '0') }}
              </template>
              
              <template v-slot:item.total="{ item }">
                ${{ item.total.toFixed(2) }}
              </template>
              
              <template v-slot:item.estado="{ item }">
                <v-chip :color="getEstadoColor(item.estado)" small>
                  {{ item.estado }}
                </v-chip>
              </template>
              
              <template v-slot:item.cliente="{ item }">
                {{ item.cliente ? item.cliente.razonSocial : 'Consumidor Final' }}
              </template>
              
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon 
                      small 
                      class="mr-2" 
                      v-bind="attrs" 
                      v-on="on"
                      @click="verProductos(item)"
                    >
                      mdi-magnify
                    </v-icon>
                  </template>
                  <span>Ver productos</span>
                </v-tooltip>
                
                <v-tooltip bottom v-if="item.estado === 'EMITIDA'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon 
                      small 
                      color="red"
                      class="mr-2"
                      v-bind="attrs" 
                      v-on="on"
                      @click="descargarPDF(item)"
                      style="cursor: pointer;"
                    >
                      mdi-file-pdf-box
                    </v-icon>
                  </template>
                  <span>Descargar PDF</span>
                </v-tooltip>

                <v-tooltip bottom v-if="puedeConsultarComprobanteAfip(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="primary"
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="consultarComprobanteAfip(item)"
                      style="cursor: pointer;"
                    >
                      mdi-cloud-search-outline
                    </v-icon>
                  </template>
                  <span>Consultar comprobante AFIP</span>
                </v-tooltip>
                
                <v-tooltip bottom v-if="envioEmailHabilitado && item.cliente && item.cliente.email">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon 
                      small 
                      class="mr-2" 
                      v-bind="attrs" 
                      v-on="on"
                      @click="enviarEmail(item)"
                    >
                      mdi-email
                    </v-icon>
                  </template>
                  <span>Enviar por Email</span>
                </v-tooltip>
                
                <v-tooltip bottom v-if="item.estado === 'ERROR'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon 
                      small 
                      class="mr-2" 
                      v-bind="attrs" 
                      v-on="on"
                      @click="procesarPendiente(item)"
                      color="warning"
                    >
                      mdi-refresh
                    </v-icon>
                  </template>
                  <span>Reintentar Emisión</span>
                </v-tooltip>
              </template>
            </v-data-table>
            </div>
          </v-tab-item>
          
          <!-- Tab: Facturar -->
          <v-tab-item>
            <facturacion-masiva modo="page" @finalizado="onMasivaFinalizada" />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
    
    <snackbar v-model="flash"/>
    
    <!-- Modal para emitir factura -->
    <factura-emitir-modal 
      v-model="mostrarModalEmitir"
      :movimiento="movimientoSeleccionado"
      :tipo-comprobante-inicial="tipoComprobanteInicial"
      @factura-emitida="onFacturaEmitida"
    />
    
    <!-- Modal para ver productos del movimiento -->
    <v-dialog v-model="dialogProductos" max-width="800">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-package-variant</v-icon>
          Productos del Movimiento
        </v-card-title>
        
        <v-card-text>
          <div v-if="movimientoDetalle" class="table-fixed-header-wrap">
          <v-data-table
            :headers="headersProductos"
            :items="movimientoDetalle.itemsCarrito"
            :loading="loadingProductos"
            fixed-header
            height="360"
            class="elevation-1"
            hide-default-footer
            disable-pagination
          >
            <template v-slot:item.producto="{ item }">
              {{ getProductoNombre(item.producto) }}
            </template>
            
            <template v-slot:item.precioBase="{ item }">
              ${{ item.precioBase ? item.precioBase.toFixed(2) : '0.00' }}
            </template>
            
            <template v-slot:item.precioTotal="{ item }">
              ${{ item.precioTotal ? item.precioTotal.toFixed(2) : '0.00' }}
            </template>
          </v-data-table>
          </div>
          
          <v-alert v-else type="info" text class="mt-4">
            No se pudo cargar la información del movimiento
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogProductos = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal para ver respuesta cruda de AFIP/ARCA -->
    <v-dialog v-model="dialogComprobanteAfip" max-width="900">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-cloud-search-outline</v-icon>
          Información del Comprobante AFIP
        </v-card-title>

        <v-card-text>
          <v-alert v-if="comprobanteAfip && !comprobanteAfip.existe" type="warning" text>
            AFIP no devolvió datos para este comprobante.
          </v-alert>

          <v-progress-linear
            v-if="loadingComprobanteAfip"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <div v-if="comprobanteAfip">
            <div class="mb-4">
              <strong>Comprobante:</strong>
              {{ String(comprobanteAfip.puntoVenta).padStart(4, '0') }}-{{ String(comprobanteAfip.numeroComprobante).padStart(8, '0') }}
              <span class="ml-2 grey--text">Tipo {{ comprobanteAfip.tipoComprobante }}</span>
            </div>

            <template v-if="afipDetalle">
              <v-row dense class="mb-4">
                <v-col cols="12" sm="6" md="3">
                  <v-card outlined class="afip-summary-card">
                    <div class="caption grey--text text--darken-1">Resultado</div>
                    <v-chip :color="getAfipResultadoColor(afipDetalle.resultado)" small dark>
                      {{ getAfipResultadoText(afipDetalle.resultado) }}
                    </v-chip>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card outlined class="afip-summary-card">
                    <div class="caption grey--text text--darken-1">CAE</div>
                    <div class="font-weight-medium">{{ afipDetalle.codAutorizacion || '-' }}</div>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card outlined class="afip-summary-card">
                    <div class="caption grey--text text--darken-1">Vencimiento</div>
                    <div class="font-weight-medium">{{ formatAfipDate(afipDetalle.fchVto) }}</div>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card outlined class="afip-summary-card">
                    <div class="caption grey--text text--darken-1">Total</div>
                    <div class="font-weight-medium">{{ formatAfipMoney(afipDetalle.impTotal) }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1 py-3">
                      <v-icon left small>mdi-file-document-outline</v-icon>
                      Datos del comprobante
                    </v-card-title>
                    <v-simple-table dense>
                      <tbody>
                        <tr>
                          <td>Tipo de emisión</td>
                          <td class="text-right">{{ afipDetalle.emisionTipo || '-' }}</td>
                        </tr>
                        <tr>
                          <td>Fecha comprobante</td>
                          <td class="text-right">{{ formatAfipDate(afipDetalle.cbteFch) }}</td>
                        </tr>
                        <tr>
                          <td>Fecha proceso</td>
                          <td class="text-right">{{ formatAfipDateTime(afipDetalle.fchProceso) }}</td>
                        </tr>
                        <tr>
                          <td>Concepto</td>
                          <td class="text-right">{{ getAfipConceptoText(afipDetalle.concepto) }}</td>
                        </tr>
                        <tr>
                          <td>Receptor</td>
                          <td class="text-right">{{ getAfipDocumentoText(afipDetalle.docTipo, afipDetalle.docNro) }}</td>
                        </tr>
                        <tr>
                          <td>Rango</td>
                          <td class="text-right">{{ afipDetalle.cbteDesde }} - {{ afipDetalle.cbteHasta }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1 py-3">
                      <v-icon left small>mdi-cash-multiple</v-icon>
                      Importes
                    </v-card-title>
                    <v-simple-table dense>
                      <tbody>
                        <tr>
                          <td>Neto gravado</td>
                          <td class="text-right">{{ formatAfipMoney(afipDetalle.impNeto) }}</td>
                        </tr>
                        <tr>
                          <td>IVA</td>
                          <td class="text-right">{{ formatAfipMoney(afipDetalle.impIVA) }}</td>
                        </tr>
                        <tr>
                          <td>No gravado</td>
                          <td class="text-right">{{ formatAfipMoney(afipDetalle.impTotConc) }}</td>
                        </tr>
                        <tr>
                          <td>Exento</td>
                          <td class="text-right">{{ formatAfipMoney(afipDetalle.impOpEx) }}</td>
                        </tr>
                        <tr>
                          <td>Tributos</td>
                          <td class="text-right">{{ formatAfipMoney(afipDetalle.impTrib) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">Total</td>
                          <td class="text-right font-weight-bold">{{ formatAfipMoney(afipDetalle.impTotal) }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                    <div class="px-4 pb-3 caption grey--text text--darken-1">
                      Moneda: {{ afipDetalle.monId || '-' }} · Cotización: {{ afipDetalle.monCotiz || '-' }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>

            </template>

            <pre v-else class="afip-response">{{ comprobanteAfip.respuesta || 'Sin respuesta' }}</pre>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogComprobanteAfip = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import FacturacionProvider from '../../providers/FacturacionProvider'
import MovimientoProvider from '../../../movimiento/providers/MovimientoProvider'
import CompanyProvider from '../../../base/providers/CompanyProvider'
import { Snackbar } from '@dracul/common-frontend'
import { DayjsMixin } from "@dracul/dayjs-frontend";
import FacturaEmitirModal from '../../components/FacturaEmitirModal'
import FacturacionMasiva from "../../components/FacturacionMasiva"

export default {
  name: 'FacturaManagementPage',

  components: {
    Snackbar,
    FacturaEmitirModal,
    FacturacionMasiva,
  },

  mixins: [DayjsMixin],
  
  computed: {
    userCompanyId() {
      return this.$store.state.user.companyId;
    },

    afipDetalle() {
      if (!this.comprobanteAfip || !this.comprobanteAfip.respuesta) return null
      try {
        return JSON.parse(this.comprobanteAfip.respuesta)
      } catch (error) {
        return null
      }
    }
  },
  
  data() {
    return {
      activeTab: 0,
      totalFacturas: 0,
      totalFacturables: 0,
      options: { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] },
      loading: false,
      loadingPendientes: false,
      facturas: [],
      pendientes: [],
      ordenesVentaPendientesCount: 0,
      flash: null,
      
      // Modal de emitir factura
      mostrarModalEmitir: false,
      movimientoSeleccionado: null,
      tipoComprobanteInicial: null,
      
      // Modal de productos
      dialogProductos: false,
      movimientoDetalle: null,
      loadingProductos: false,

      // Modal de comprobante AFIP
      dialogComprobanteAfip: false,
      loadingComprobanteAfip: false,
      comprobanteAfip: null,
      envioEmailHabilitado: false,
      
      filters: {
        estado: null,
        tipoComprobante: null
      },
      
      headers: [
        { text: 'Fecha', value: 'fechaEmision' },
        { text: 'Tipo', value: 'tipoComprobante' },
        { text: 'Número', value: 'numero' },
        { text: 'Cliente', value: 'cliente' },
        { text: 'Total', value: 'total' },
        { text: 'CAE', value: 'CAE' },
        { text: 'Estado', value: 'estado' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      
      headersPendientes: [
        { text: 'Fecha', value: 'createdAt' },
        { text: 'Total', value: 'total' },
        { text: 'Error', value: 'ultimoError' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      
      headersProductos: [
        { text: 'Producto', value: 'producto' },
        { text: 'Cantidad', value: 'cantidad' },
        { text: 'Precio Unitario', value: 'precioBase' },
        { text: 'Precio Total', value: 'precioTotal' }
      ],
      
      estados: [
        { text: 'Pendiente', value: 'PENDIENTE' },
        { text: 'Emitida', value: 'EMITIDA' },
        { text: 'Error', value: 'ERROR' },
        { text: 'Procesando', value: 'PROCESANDO' }
      ],
      
      tiposComprobante: [
        { text: 'Factura A', value: 'FACTURA_A' },
        { text: 'Factura B', value: 'FACTURA_B' },
        { text: 'Factura C', value: 'FACTURA_C' },
        { text: 'Factura X', value: 'FACTURA_X' }
      ]
    }
  },
  
  watch: {
    filters: {
      deep: true,
      handler() {
        this.options.page = 1
        this.loadFacturas()
      }
    },
    options: {
      deep: true,
      handler() {
        this.loadFacturas()
      }
    },
    activeTab(valor) {
      // La tab viaja en la URL: sobrevive a un F5 y se puede linkear.
      const tab = valor === 1 ? "masiva" : "historial"
      if (this.$route.query.tab !== tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } })
      }
    }
  },
  
  mounted() {
    this.activeTab = this.$route.query.tab === "masiva" ? 1 : 0
    this.cargarCompanyStatus()
    this.loadFacturas()
    this.loadPendientes()
    this.loadFacturables()
  },
  
  methods: {
    async cargarCompanyStatus() {
      try {
        const response = await CompanyProvider.companyAfipStatus()
        const st = response.data && response.data.companyAfipStatus
        this.envioEmailHabilitado = st ? st.envioEmailHabilitado !== false : false
      } catch (e) {
        this.envioEmailHabilitado = false
      }
    },
    async loadFacturas() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options
        const response = await FacturacionProvider.paginateFacturas(
          page,
          itemsPerPage,
          null,
          this.filters,
          sortBy && sortBy.length ? sortBy[0] : null,
          Boolean(sortDesc && sortDesc[0])
        )
        this.facturas = response.data.facturaPaginate.items
        this.totalFacturas = response.data.facturaPaginate.totalItems
      } catch (error) {
        console.error('Error cargando facturas:', error)
      } finally {
        this.loading = false
      }
    },
    
    async loadPendientes() {
      if (!this.userCompanyId) {
        console.warn('No se puede cargar pendientes sin companyId');
        return;
      }

      this.loadingPendientes = true
      try {
        const response = await FacturacionProvider.getFacturasPendientes(this.userCompanyId)
        this.pendientes = response.data.facturasPendientes
      } catch (error) {
        console.error('Error cargando pendientes:', error)
      } finally {
        this.loadingPendientes = false
      }
    },

    async onMasivaFinalizada() {
      await Promise.all([this.loadFacturas(), this.loadPendientes(), this.loadFacturables()])
    },

    // El badge de la tab cuenta lo que realmente se puede facturar, no las
    // facturas ya creadas en estado PENDIENTE.
    async loadFacturables() {
      try {
        const r = await FacturacionProvider.facturacionMasivaResumen({})
        this.totalFacturables = r.data.facturacionMasivaResumen.totalAptas
      } catch (e) {
        this.totalFacturables = 0
      }
    },
    
    getEstadoColor(estado) {
      const colors = {
        EMITIDA: 'success',
        PENDIENTE: 'warning',
        ERROR: 'error',
        PROCESANDO: 'info'
      }
      return colors[estado] || 'grey'
    },
    
    getTipoComprobanteText(tipo) {
      const tipos = {
        FACTURA_A: 'Factura A',
        FACTURA_B: 'Factura B',
        FACTURA_C: 'Factura C',
        NOTA_CREDITO_A: 'Nota de Crédito A',
        NOTA_CREDITO_B: 'Nota de Crédito B',
        NOTA_CREDITO_C: 'Nota de Crédito C'
      }
      return tipos[tipo] || tipo
    },

    getAfipResultadoColor(resultado) {
      const colors = {
        A: 'success',
        R: 'error',
        P: 'warning'
      }
      return colors[resultado] || 'grey'
    },

    getAfipResultadoText(resultado) {
      const resultados = {
        A: 'Autorizado',
        R: 'Rechazado',
        P: 'Parcial'
      }
      return resultados[resultado] || (resultado || '-')
    },

    getAfipConceptoText(concepto) {
      const conceptos = {
        1: 'Productos',
        2: 'Servicios',
        3: 'Productos y Servicios'
      }
      return conceptos[concepto] || concepto || '-'
    },

    getAfipDocumentoText(tipo, numero) {
      const tipos = {
        80: 'CUIT',
        86: 'CUIL',
        96: 'DNI',
        99: 'Consumidor Final'
      }
      const tipoTexto = tipos[tipo] || `Doc. ${tipo || '-'}`
      if (!numero) return tipoTexto
      return `${tipoTexto} ${numero}`
    },

    formatAfipDate(value) {
      const str = value ? String(value) : ''
      if (str.length !== 8) return str || '-'
      return `${str.substring(6, 8)}/${str.substring(4, 6)}/${str.substring(0, 4)}`
    },

    formatAfipDateTime(value) {
      const str = value ? String(value) : ''
      if (str.length !== 14) return this.formatAfipDate(str)
      return `${str.substring(6, 8)}/${str.substring(4, 6)}/${str.substring(0, 4)} ${str.substring(8, 10)}:${str.substring(10, 12)}:${str.substring(12, 14)}`
    },

    formatAfipMoney(value) {
      const amount = Number(value)
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS'
      }).format(Number.isFinite(amount) ? amount : 0)
    },

    puedeConsultarComprobanteAfip(factura) {
      return factura.estado === 'EMITIDA' && factura.tipoComprobante !== 'FACTURA_X'
    },

    async consultarComprobanteAfip(factura) {
      const id = factura.id || factura._id
      if (!id) return

      this.loadingComprobanteAfip = true
      this.dialogComprobanteAfip = true
      this.comprobanteAfip = null

      try {
        const response = await FacturacionProvider.consultarComprobanteAfip(id)
        this.comprobanteAfip = response.data.facturaComprobanteAfipInfo
      } catch (error) {
        console.error('Error consultando comprobante AFIP:', error)
        this.flash = `Error consultando comprobante AFIP: ${error.message}`
        this.dialogComprobanteAfip = false
      } finally {
        this.loadingComprobanteAfip = false
      }
    },
    
    async enviarEmail(factura) {
      try {
        this.loading = true
        await FacturacionProvider.enviarEmail(factura.id)
        
        this.flash = `Factura enviada a ${factura.cliente.email}`
        
        // Recargar lista
        await this.loadFacturas()
      } catch (error) {
        this.flash = `Error enviando email: ${error.message}`
      } finally {
        this.loading = false
      }
    },
    
    async procesarPendiente(factura) {
      try {
        this.loading = true
        
        // Obtener el movimiento completo
        const response = await MovimientoProvider.findMovimiento(factura.movimientoId)
        this.movimientoSeleccionado = response.data.movimientoFind
        
        // No pre-cargar tipo de comprobante si es FACTURA_C (valor por defecto)
        // El usuario debe seleccionar el tipo correcto
        this.tipoComprobanteInicial = null
        
        // Abrir el modal para que el usuario confirme o modifique la información
        this.mostrarModalEmitir = true
        
      } catch (error) {
        this.flash = `Error cargando movimiento: ${error.message}`
      } finally {
        this.loading = false
      }
    },
    
    async onFacturaEmitida() {
      // Cerrar modal
      this.mostrarModalEmitir = false
      this.movimientoSeleccionado = null
      this.tipoComprobanteInicial = null
      
      // Mostrar mensaje de éxito
      this.flash = 'Factura procesada correctamente'
      
      // Recargar listas
      await Promise.all([this.loadFacturas(), this.loadPendientes()])
    },
    
    async verProductos(factura) {
      try {
        this.loadingProductos = true
        this.dialogProductos = true
        
        // Obtener el movimiento completo con sus productos
        const response = await MovimientoProvider.findMovimiento(factura.movimientoId)
        this.movimientoDetalle = response.data.movimientoFind
        
      } catch (error) {
        console.error('Error cargando productos:', error)
        this.flash = 'Error al cargar los productos del movimiento'
        this.dialogProductos = false
      } finally {
        this.loadingProductos = false
      }
    },
    
    getProductoNombre(producto) {
      if (!producto) return 'N/A'
      if (producto.nombre) return producto.nombre
      
      const marca = producto.marca?.nombre || ''
      const modelo = producto.modelo?.nombre || ''
      
      if (marca && modelo) {
        return `${marca} ${modelo}`
      }
      
      return marca || modelo || 'N/A'
    },
    
    async descargarPDF(factura) {
      const id = factura.id || factura._id
      if (!id) return
      try {
        const r = await FacturacionProvider.generarPDFBase64(id)
        const result = r?.data?.facturaGenerarPDFBase64
        if (!result?.base64) {
          this.flash = 'No se pudo generar el PDF'
          return
        }
        const base64 = result.base64
        const filename = result.filename || 'Factura.pdf'
        const byteCharacters = atob(base64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
        this.flash = 'Descarga iniciada'
      } catch (error) {
        console.error('Error descargando PDF:', error)
        this.flash = `Error al descargar PDF: ${error.message}`
      }
    }
  }
}
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}

.afip-summary-card {
  min-height: 88px;
  padding: 14px;
}

.afip-response {
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 460px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
